import { Injectable, inject } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { map, Observable } from 'rxjs';
import {
  CreateSecurityRequest,
  GstarClient,
  ReevaluateSecurityRequest,
  SecurityByIdResponseWithNestedObjectsAuditable,
  SecurityResponseAuditable,
  SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse,
} from '../../../web-api-client';
import { Collection } from '../../admin/models';
import { mapSecurityDetailsFromResponse } from '../../tax-result/mappers/security/map-security-details-from-response';
import { mapSecurityFromResponse } from '../mappers';
import { SecurityDetails } from '../models/security-details';
import { SecurityGridData } from '../models/security-grid-data';
import { TaxResult } from '../models/taxResult';

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  get(
    query?: Partial<QueryOptions<SecurityGridData>>
  ): Observable<Collection<SecurityGridData>> {
    return this.gstarClient
      .byclient3(
        this.headerService.selectedClient()?.id,
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(
          (
            response: SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse
          ) => ({
            total: response.odataCount ?? 0,
            items:
              response.value?.map(response =>
                mapSecurityFromResponse(response)
              ) ?? [],
          })
        )
      );
  }

  createSecurity(
    securityRequest: CreateSecurityRequest
  ): Observable<SecurityResponseAuditable> {
    return this.gstarClient.securitiesPOST(securityRequest);
  }

  updateSecurity(
    securityId: number,
    securityRequest: ReevaluateSecurityRequest
  ): Observable<void> {
    return this.gstarClient.reevaluate(securityId, securityRequest);
  }

  getSecurityById(id: number): Observable<SecurityDetails> {
    return this.gstarClient
      .securitiesGET3(id)
      .pipe(
        map((response: SecurityByIdResponseWithNestedObjectsAuditable) =>
          mapSecurityDetailsFromResponse(response)
        )
      );
  }

  groupBy(
    arr: TaxResult[],
    key: (i: TaxResult) => number
  ): Record<number, TaxResult[]> {
    return arr
      .sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0))
      .reduce(
        (groups, item) => {
          (groups[key(item)] ||= []).push(item);
          return groups;
        },
        {} as Record<number, TaxResult[]>
      );
  }
}
